import { Link } from "react-router-dom";
import { MapPin, Clock, ChevronDown, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import CountdownTimer from "@/components/CountdownTimer";

const COUPLE_PHOTO =
  "https://customer-assets.emergentagent.com/job_guest-connect-16/artifacts/1g64vunr_IMG_0195.jpg";
const VENUE_IMG =
  "https://www.tenutaroverbella.it/wp-content/uploads/2020/03/Roverbella-slide1-1024x683.jpg";
const VENUE_IMG2 =
  "https://www.tenutaroverbella.it/wp-content/uploads/2014/11/KL7oc-1024x683.jpeg";

export default function HomePage() {
  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="hero-section" data-testid="hero-section">
        <div
          className="hero-bg"
          style={{ backgroundImage: `url(${COUPLE_PHOTO})` }}
        />
        <div className="hero-overlay" />
        <div className="hero-content px-6 max-w-3xl mx-auto">
          <p
            className="text-sm sm:text-base tracking-[0.3em] uppercase mb-4 animate-fade-in-up animation-delay-200"
            style={{ color: "rgba(255,255,255,0.8)" }}
          >
            Ci sposiamo
          </p>
          <h1
            className="font-serif text-5xl sm:text-6xl lg:text-7xl font-semibold mb-4 animate-fade-in-up animation-delay-400"
            style={{ color: "#FFFFFF" }}
          >
            Marianna{" "}
            <span style={{ color: "#C5A572" }}>&</span>{" "}
            Marco
          </h1>
          <div className="gold-line animate-fade-in-up animation-delay-600" />
          <p
            className="text-lg sm:text-xl mt-4 font-serif italic animate-fade-in-up animation-delay-600"
            style={{ color: "#C5A572" }}
          >
            5 Giugno 2026
          </p>
          <div className="mt-8 animate-fade-in-up animation-delay-800">
            <Link to="/rsvp">
              <Button
                data-testid="hero-cta-button"
                className="btn-press touch-target px-8 py-3 text-base rounded-sm"
                style={{
                  backgroundColor: "#8B9A6B",
                  color: "white",
                  minHeight: "48px",
                }}
              >
                Conferma la tua presenza
              </Button>
            </Link>
          </div>
        </div>
        <div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 scroll-indicator"
          style={{ color: "rgba(255,255,255,0.6)" }}
        >
          <ChevronDown size={28} />
        </div>
      </section>

      {/* COUNTDOWN */}
      <section
        className="section-padding bg-warm-white"
        data-testid="countdown-section"
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-2xl sm:text-3xl mb-2" style={{ color: "#2C2C2C" }}>
            Quanto manca
          </h2>
          <div className="gold-line mb-10" />
          <CountdownTimer />
        </div>
      </section>

      {/* CEREMONY & RECEPTION */}
      <section
        className="section-padding"
        style={{ backgroundColor: "#F5E6D3" }}
        data-testid="info-section"
      >
        <div className="max-w-5xl mx-auto">
          <h2
            className="font-serif text-2xl sm:text-3xl text-center mb-2"
            style={{ color: "#2C2C2C" }}
          >
            La giornata
          </h2>
          <div className="gold-line mb-12" />

          <div className="grid md:grid-cols-2 gap-10">
            {/* Ceremony */}
            <div
              data-testid="ceremony-info"
              className="bg-white rounded-sm p-8 shadow-sm text-center"
              style={{ border: "1px solid rgba(197, 165, 114, 0.3)" }}
            >
              <h3
                className="font-serif text-xl sm:text-2xl mb-1"
                style={{ color: "#2C2C2C" }}
              >
                Cerimonia Civile
              </h3>
              <div className="gold-line mb-6" />
              <div className="flex items-center justify-center gap-2 mb-3">
                <Clock size={18} className="text-gold" />
                <span className="text-base" style={{ color: "#2C2C2C" }}>
                  Ore 14:00
                </span>
              </div>
              <div className="flex items-start justify-center gap-2 mb-3">
                <MapPin size={18} className="text-gold flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-base font-medium" style={{ color: "#2C2C2C" }}>
                    Casa Gola
                  </p>
                  <p className="text-sm" style={{ color: "#666666" }}>
                    Piazza IV Novembre, Lucino - 20090 Rodano
                  </p>
                </div>
              </div>
              <a
                href="https://maps.google.com/?q=Casa+Gola+Piazza+IV+Novembre+Lucino+Rodano"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-sage hover:underline mt-2"
                data-testid="ceremony-map-link"
              >
                Indicazioni <ExternalLink size={14} />
              </a>
            </div>

            {/* Reception */}
            <div
              data-testid="reception-info"
              className="bg-white rounded-sm p-8 shadow-sm text-center"
              style={{ border: "1px solid rgba(197, 165, 114, 0.3)" }}
            >
              <h3
                className="font-serif text-xl sm:text-2xl mb-1"
                style={{ color: "#2C2C2C" }}
              >
                Ricevimento
              </h3>
              <div className="gold-line mb-6" />
              <div className="flex items-center justify-center gap-2 mb-3">
                <Clock size={18} className="text-gold" />
                <span className="text-base" style={{ color: "#2C2C2C" }}>
                  Ore 16:00
                </span>
              </div>
              <div className="flex items-start justify-center gap-2 mb-3">
                <MapPin size={18} className="text-gold flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-base font-medium" style={{ color: "#2C2C2C" }}>
                    Tenuta Roverbella
                  </p>
                  <p className="text-sm" style={{ color: "#666666" }}>
                    Cascina Roverbella, Pantigliate (MI)
                  </p>
                </div>
              </div>
              <a
                href="https://maps.google.com/?q=Tenuta+Roverbella+Pantigliate"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-sage hover:underline mt-2"
                data-testid="reception-map-link"
              >
                Indicazioni <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* VENUE GALLERY */}
      <section className="section-padding bg-warm-white" data-testid="venue-section">
        <div className="max-w-5xl mx-auto">
          <h2
            className="font-serif text-2xl sm:text-3xl text-center mb-2"
            style={{ color: "#2C2C2C" }}
          >
            La Location
          </h2>
          <div className="gold-line mb-10" />
          <div className="grid md:grid-cols-2 gap-4">
            <img
              src={VENUE_IMG}
              alt="Tenuta Roverbella"
              className="w-full h-64 md:h-80 object-cover rounded-sm"
              loading="lazy"
            />
            <img
              src={VENUE_IMG2}
              alt="Tenuta Roverbella giardino"
              className="w-full h-64 md:h-80 object-cover rounded-sm"
              loading="lazy"
            />
          </div>
          <div className="text-center mt-6">
            <a
              href="https://www.tenutaroverbella.it"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-sage hover:underline"
              data-testid="venue-website-link"
            >
              Visita il sito della location <ExternalLink size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="section-padding text-center"
        style={{ backgroundColor: "#F5E6D3" }}
        data-testid="cta-section"
      >
        <div className="max-w-2xl mx-auto">
          <h2
            className="font-serif text-2xl sm:text-3xl mb-2"
            style={{ color: "#2C2C2C" }}
          >
            Vi aspettiamo!
          </h2>
          <div className="gold-line mb-6" />
          <p className="text-base mb-8" style={{ color: "#666666" }}>
            Confermate la vostra presenza per aiutarci a organizzare al meglio
            il nostro giorno speciale.
          </p>
          <Link to="/rsvp">
            <Button
              data-testid="cta-rsvp-button"
              className="btn-press touch-target px-10 py-3 text-base rounded-sm"
              style={{
                backgroundColor: "#8B9A6B",
                color: "white",
                minHeight: "48px",
              }}
            >
              Conferma la tua presenza
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
}
